import { env } from '@/config/env';

import { ApiError, toApiError } from './ApiError';

/**
 * The Turnstile actions, one per public form. They must match the constants in
 * supabase/functions (submit-website-enquiry, submit-checkout); a mismatch makes
 * every submission on that form fail the server's check.
 */
export const CAPTCHA_ACTIONS = {
  enquiry: 'website_enquiry',
  carOrder: 'car_order',
  accessoryOrder: 'accessory_order',
} as const;

const GENERIC = 'We could not send your request. Please try again, or contact us on WhatsApp.';

/**
 * Posts a public form to its Edge Function. The anon key identifies the project
 * and is public by design; the function itself checks the captcha token in the
 * body, so no session is involved.
 *
 * Messages come from the server only when the server wrote them for a visitor
 * (validation, rate limits, the captcha prompt). Anything else is generic.
 */
export async function postPublicSubmission<T extends { reference_no: string }>(
  slug: string,
  body: Record<string, unknown>,
): Promise<T> {
  if (!env.supabaseUrl || !env.supabaseAnonKey) {
    throw new ApiError('unavailable', GENERIC);
  }

  try {
    const response = await fetch(`${env.supabaseUrl}/functions/v1/${slug}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: env.supabaseAnonKey,
        Authorization: `Bearer ${env.supabaseAnonKey}`,
      },
      body: JSON.stringify(body),
    });

    const json = (await response.json().catch(() => null)) as
      (Partial<T> & { error?: string }) | null;
    if (response.ok && json?.reference_no) return json as T;

    throw new ApiError('unavailable', json?.error ?? GENERIC);
  } catch (error) {
    throw toApiError(error);
  }
}
